import { computed, type Ref } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { ApiError } from "@/helpers/api-error";

const BASE = `${import.meta.env.VITE_API_URL}/admin/dead-letters`;

/** A message the broker gave up on, as the superadmin list shows it. */
export interface DeadLetter {
  id: string;
  orgId: string;
  /** Which queue gave up — a lane chunk and an admission release fail differently. */
  entity: string;
  /** What the message was, or `Unreadable` when the body is not an event we can read. */
  eventType: string;
  /** Service Bus's own reason, e.g. `MaxDeliveryCountExceeded` or `TTLExpiredException`. */
  reason: string;
  description: string;
  /** The message as sent. The evidence, and what a replay would take. */
  body: string;
  recordedAt: string;
  workflowRunId?: string | null;
  serviceId?: string | null;
  /** Absent when the message named no run, or the run can no longer be read. */
  workflowId?: string | null;
  workflowName?: string | null;
  projectId?: string | null;
  runStatus?: string | null;
}

export interface UseDeadLettersOptions {
  /** Narrow to one tenant. Omitted, the read walks every org — the only way to see a record naming no run. */
  orgId?: Ref<string | undefined>;
  /** Narrow to one run within that org. */
  runId?: Ref<string | undefined>;
  /** How many days back. Omitted, the server applies its own window to an unnarrowed read. */
  days?: Ref<number | undefined>;
  /** Rows at most. Omitted, the server's limit applies. */
  take?: Ref<number | undefined>;
}

/** Row limit the server applies when the caller names none — mirrored so the view can say it capped. */
export const DEAD_LETTER_DEFAULT_TAKE = 100;

/** Window options for the estate view. Matches the server's own default at 7. */
export const DEAD_LETTER_WINDOW_OPTIONS = [
  { value: 1, label: "Last 24 hours" },
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 0, label: "All time" },
];

/**
 * Superadmin only — the endpoint carries the policy and the handler refuses anyway. Normally empty:
 * nothing dead-letters in ordinary operation, so anything here wants a person.
 */
export function useDeadLetters(options: UseDeadLettersOptions = {}) {
  const url = computed(() => {
    const params = new URLSearchParams();
    if (options.orgId?.value) params.set("orgId", options.orgId.value);
    if (options.runId?.value) params.set("runId", options.runId.value);
    // Always sent, because omitting it lets the server apply its own window to an unnarrowed read — so
    // "all time" has to be said rather than left unsaid, and the epoch is how you say it.
    if (options.days !== undefined) {
      const since = options.days.value
        ? new Date(Date.now() - options.days.value * 86_400_000)
        : new Date(0);
      params.set("since", since.toISOString());
    }
    if (options.take?.value) params.set("take", String(options.take.value));
    const qs = params.toString();
    return qs ? `${BASE}?${qs}` : BASE;
  });

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: [
      "admin-dead-letters",
      options.orgId,
      options.runId,
      options.days,
      options.take,
    ],
    queryFn: () => fetchWrapper.get<DeadLetter[]>(url.value),
  });

  return {
    deadLetters: computed(() => data.value ?? []),
    deadLettersLoading: isFetching,
    deadLettersError: error as Ref<ApiError | null>,
    refetchDeadLetters: refetch,
  };
}

interface DiscardPayload {
  orgId: string;
  ids: string[];
}

/**
 * Stops keeping the named records, and answers how many were still there. This discards the last evidence
 * of a lost message — the broker's copy is long gone by the time a record exists — so the caller confirms.
 */
export function useDiscardDeadLetters() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending, error } = useMutation<
    number,
    ApiError,
    DiscardPayload
  >({
    mutationFn: (payload) => fetchWrapper.post(`${BASE}/discard`, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin-dead-letters"] }),
  });

  return {
    discardDeadLetters: mutateAsync,
    discardPending: isPending,
    discardError: error as Ref<ApiError | null>,
  };
}
